import {
  HosApiOperator,
  HosApiOperatorMediaRoomProfile,
  HosApiOperatorMediaRoomSubscriberGroup,
  HosApiOperatorSystemType,
  ResApiOperator,
} from '../types/apiTypes';
import { mapCloudDvr } from '../hooks/CloudDvrs/useCloudDvrs';
import { mapExperienceGroup } from '../hooks/ExperienceGroups/useExperienceGroups';
import { mapRoutingGroup } from '../hooks/RoutingGroups/useRoutingGroups';
import { mapRoomGroup } from '../hooks/RoomGroups/useRoomGroups';
import { mapRoomType } from '../hooks/RoomTypes/useRoomTypes';
import { mapLocation } from '../hooks/Locations/useLocations';
import { mapAccountSite } from '../hooks/AccountSites/useAccountSites';
import {
  mapHosDeviceModel,
  mapResDeviceModel,
} from '../hooks/DeviceModels/useDeviceModels';
import {
  mapHosTransportType,
  mapResTransportType,
} from '../hooks/TransportType/useTransportTypes';
import { mapFIPSCode } from '../hooks/FipsCodes/useFipsCodes';
import { BASE_URL, getBaseUrl } from '../lib/environment';

export const sortByFipsCode = (a: FipsCode, b: FipsCode) =>
  a.county < b.county ? -1 : 1;
export const sortByMediafirstId = (
  a: { mediafirstId: string },
  b: { mediafirstId: string }
) => (a.mediafirstId < b.mediafirstId ? -1 : 1);
export const sortByName = (a: any, b: any) => (a.name < b.name ? -1 : 1);
export const sortByDescription = (a: any, b: any) =>
  a.description < b.description ? -1 : 1;
export const sortByKey = (a: MediaroomOverride, b: MediaroomOverride) =>
  a.key < b.key ? -1 : 1;

export const mapHosOperator = (operator: HosApiOperator): Operator => ({
  id: operator.ID,
  baseUrl: BASE_URL + 'hos' + getBaseUrl(false),
  code: operator.Code,
  deviceTypes: operator.DeviceTypes.map((d) => ({
    id: d.ID,
    active: d.Active,
    name: d.Name,
    system: d.SystemType.Name,
  })),
  bacCode: operator.BacCode,
  locations: operator.Locations.map((l) => mapLocation(l)).sort(sortByName),
  models: operator.DeviceModels.map((d) => mapHosDeviceModel(d)).sort(
    sortByName
  ),
  mr: operator.MediaRoomProfile
    ? mapMediaroomProfile(operator.MediaRoomProfile)
    : null,
  name: operator.Name,
  provisioningEndpoint: operator.ProvisioningEndpoint,
  proxyEndpoint: operator.ProxyEndpoint,
  roomGroups: operator.RoomGroups.map((r) => mapRoomGroup(r)).sort(sortByName),
  roomTypes: operator.RoomTypes.map((r) => mapRoomType(r)).sort(sortByName),
  sites: operator.Sites.map((s) => mapAccountSite(s)).sort(sortByName),
  systems: operator.SystemTypes.map((s) => mapSystemType(s)),
  timeZone: operator.TimeZone,
  timeZoneIANA: operator.TimeZoneIANA,
  transports: operator.TransportTypes.map((t) => mapHosTransportType(t)),
  type: 'Hospitality',
});

export const mapResOperator = (operator: ResApiOperator): Operator => {
  let systemMap = [] as System[];
  if (operator.MediaRoom)
    systemMap.push({ id: 0, name: 'Residential MediaRoom', active: true });
  if (operator.MediaFirst)
    systemMap.push({ id: 0, name: 'Residential MediaFirst', active: true });
  return {
    id: operator.ID,
    appKeyUrl1: operator.AppKeyURL1,
    appKeyUrl2: operator.AppKeyURL2,
    appKeyUrl3: operator.AppKeyURL3,
    appKeyUrl4: operator.AppKeyURL4,
    availableTimeZones: (operator.AvailableTimeZones ?? []).sort((a, b) =>
      a < b ? -1 : 1
    ),
    baseUrl: BASE_URL + 'res' + getBaseUrl(false),
    branch: operator.Branch
      ? {
          id: operator.Branch.ID,
          name: operator.Branch.Name,
          mediaroomIp: operator.Branch.MediaroomIp,
        }
      : undefined,
    channelMapGroups: operator.ChannelMapGroups.map((g) =>
      mapChannelMapGroup(g)
    ).sort(sortByDescription),
    cloudDVRs: operator.CloudDVRs.map((d) => mapCloudDvr(d)).sort(
      sortByMediafirstId
    ),
    code: operator.Code,
    creditResetDay: operator.CreditResetDay,
    defaultCreditLimit: operator.DefaultCreditLimit,
    defaultExperienceGroupId: operator.DefaultExperienceGroupID,
    defaultRoutingGroupId: operator.DefaultRoutingGroupID,
    defaultPins: operator.DefaultPins.map((pin) => mapDefaultPin(pin)),
    enhancedData: operator.EnhancedData,
    experienceGroups: operator.ExperienceGroups.map((e) =>
      mapExperienceGroup(e)
    ).sort(sortByDescription),
    fips: operator.FIPSCodes.map((c) => mapFIPSCode(c)).sort(sortByFipsCode),
    gratisAccounts: operator.GratisAccounts,
    models: operator.DeviceModels.map((d) => mapResDeviceModel(d)).sort(
      sortByName
    ),
    mediafirst: operator.MediaFirst,
    mediaroom: operator.MediaRoom,
    mediaroomOverrides: operator.MediaRoomOverrides.map(
      (override: ApiResMediaroomOverride) => mapMediaroomOverride(override)
    ).sort(sortByKey),
    name: operator.Name,
    packages: operator.SubscriberGroups.map((sg) =>
      mapSubscriberGroup(sg)
    ).sort(sortByName),
    routingGroups: operator.RoutingGroups.map((rg) => mapRoutingGroup(rg)),
    systems: systemMap,
    mediaFirstFreeChannelGroup: operator.MediaFirstFreeChannelGroup,
    mediaFirstTenantId: operator.MediaFirstTenantID,
    mediaFirstSubTenantId: operator.MediaFirstSubtenantID,
    timeZone: operator.TimeZone,
    transports: operator.TransportTypes.map((t) => mapResTransportType(t)).sort(
      sortByName
    ),
    type: 'Residential',
    usesTgOAuth: operator.UsesTGOAuth,
    showSubscribedChannelsOnly: operator.ShowSubscribedChannelsOnly,
    usesScreensaver: operator.UseScreensaver,
  };
};

export const mapChannelMapGroup = (
  group: ApiResChannelMapGroup
): ChannelMapGroup => ({
  id: group.ID,
  mediaFirstId: group.MediaFirstID,
  description: group.Description,
  subscriberGroupIDs: group.SubscriberGroupIDs,
  active: group.Active,
});

export const mapDefaultPin = (pin: ApiResDefaultPin): DefaultPin => ({
  pinTypeId: pin.PinTypeId,
  value: pin.Value,
});

export const mapMediaroomOverride = (
  override: ApiResMediaroomOverride
): MediaroomOverride => ({
  key: override.Key,
  overrideTypeId: override.OverrideTypeId,
  value: override.Value,
});

export const mapMediaroomProfile = (
  mrProfile: HosApiOperatorMediaRoomProfile
): OperatorMediaroomProfile => ({
  appKeyUrl1: mrProfile.AppKeyURL1,
  appKeyUrl2: mrProfile.AppKeyURL2,
  appKeyUrl3: mrProfile.AppKeyURL3,
  appKeyUrl4: mrProfile.AppKeyURL4,
  creditLimit: mrProfile.CreditLimit,
  rdpProfileName: mrProfile.RDPProfileName,
  packages: mrProfile.MediaRoomSubscriberGroups.map((g) =>
    mapMediaRoomSubscriberGroup(g)
  ).sort(sortByDescription),
  pollDiagnostics: mrProfile.PollDiagnostics,
  vodPrivileges: mrProfile.VODPrivileges.map((v) => ({
    id: v.ID,
    active: v.Active,
    name: v.Name,
  })),
  weatherLocation: mrProfile.WeatherLocation,
});

export const mapMediaRoomSubscriberGroup = (
  subscriberGroup: HosApiOperatorMediaRoomSubscriberGroup
): OperatorMediaroomSubscriberGroup => ({
  id: subscriberGroup.ID,
  active: subscriberGroup.Active,
  description: subscriberGroup.Description,
  mediaroomId: subscriberGroup.MediaRoomID,
  sortOrder: subscriberGroup.SortOrder,
});

export const mapSubscriberGroup = (
  subscriberGroup: ApiResSubscriberGroup
): OperatorSubscriberGroup => ({
  id: subscriberGroup.ID,
  active: subscriberGroup.Active,
  description: subscriberGroup.Description,
  name: subscriberGroup.Name,
  type: subscriberGroup.Base
    ? 'base'
    : subscriberGroup.AlaCarte
    ? 'alaCarte'
    : 'addons',
});

export const mapSystemType = (
  systemType: HosApiOperatorSystemType
): System => ({
  id: systemType.ID,
  name: systemType.Name,
  active: systemType.Active,
});
